import React from 'react'
import { ActivityIndicator } from 'react-native'

import HeaderHeightMargin from '@zet/common/components/header/headerHeightMargin'
import TransparentHeader from '@zet/common/components/header/transparentHeader'
import LazyHOC from '@zet/common/components/lazyHoc'
import BrandEligible from '@zet/common/components/loader/brandEligible'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import { BTN, SH1, colors as zetColors } from '@zet/design-system'
import { B2, Icon } from '@zet/design-system/index'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { Customer } from '@zet/feature-brands/api/brands/types'
import { i18n } from '@zet/localization'
import { LinearGradient } from 'expo-linear-gradient'
import _ from 'lodash'
import { FlatList } from 'react-native-gesture-handler'

import EligibleCustomerCard from './eligibleCustomercard'
import useBrandEligibleCustomers from './useBrandEligibleCustomers'

const SellCard = () => {
  const {
    brandCategory,
    brandName,
    dataList,
    handleChange,
    navigate,
    isHold,
    brandId,
    isLoading,
    searchText,
    clearSearch,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useBrandEligibleCustomers()

  const EligibleCardComponent = ({ value }: { value: Customer }) => (
    <EligibleCustomerCard
      customerData={value}
      source={'appBrand'}
      brandId={brandId}
      isHold={isHold}
      brandName={brandName}
      brandCategory={brandCategory}
    />
  )

  const NoDataFound = () => {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <SH1>{i18n.t('noData')}</SH1>
        {searchText !== '' ? (
          <Pressable
            onPress={clearSearch}
            className="bg-primary-100 mt-3 rounded px-3 py-2"
          >
            <BTN className="text-white">{i18n.t('myLeads.clear')}</BTN>
          </Pressable>
        ) : (
          <></>
        )}
      </Stack>
    )
  }

  const handleNavigation = () =>
    navigate('KnowYourCustomer', { source: 'brandCustomerEligibleTab' })

  const onReachEnd = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage()
    }
  }
  return (
    <>
      <LinearGradient
        colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <TransparentHeader
          showBackIcon
          title={i18n.t('brandDetails.buttontext', {
            category: brandName,
          })}
        />
        <HeaderHeightMargin>
          <Stack className="-mt-3  px-3">
            <Input
              iconLeft={<Icon type="SearchOutlined" color="white" />}
              color={zetColors.neutral.white100}
              selectionColor={zetColors.grays[60]}
              className="text-neutral-white100 mb-3 border-[#f5f7fb7c] bg-[#1A304D]"
              placeholder={i18n.t('myCustomers.placeholder')}
              frameStyle={{
                backgroundColor: '#1A304D',
                borderColor: '#f5f7fb7c',
                marginBottom: 16,
              }}
              value={searchText}
              onChangeText={handleChange}
            />
          </Stack>
        </HeaderHeightMargin>
      </LinearGradient>
      {isLoading ? (
        Array.from({ length: 4 }).map((_, index) => (
          <BrandEligible key={index} />
        ))
      ) : (
        <LazyHOC>
          {!isLoading &&
          _.isEmpty(dataList?.allCustomers) &&
          _.isEmpty(dataList?.highApprovalCustomers) ? (
            <NoDataFound />
          ) : (
            <FlatList
              className="flex-1 bg-white px-3 pt-3"
              data={dataList?.allCustomers}
              renderItem={({ item, index }) => (
                <React.Fragment key={index}>
                  {index === 0 && (
                    <SH1 className="text-grays-100 mb-3">
                      {i18n.t('eligiblecustomers.allOffers')}
                    </SH1>
                  )}
                  <EligibleCardComponent value={item} />
                </React.Fragment>
              )}
              ListFooterComponent={
                isFetchingNextPage ? (
                  <ActivityIndicator style={{ marginTop: 18 }} size={'large'} />
                ) : (
                  <></>
                )
              }
              onEndReached={onReachEnd}
              refreshing={isFetching && !isFetchingNextPage}
              onEndReachedThreshold={0.6}
              ListEmptyComponent={isLoading ? <BrandEligible /> : <></>}
              ListHeaderComponent={
                <Stack className="flex-1">
                  {dataList?.highApprovalCustomers?.map((value, index) => (
                    <React.Fragment key={index}>
                      {index === 0 && (
                        <XStack className="mb-3 items-center space-x-2">
                          <Icon
                            type="Star"
                            color={zetColors.decorative.navel}
                          />
                          <SH1 className="text-grays-100">
                            {i18n.t('eligiblecustomers.highApprovalOffers')}
                          </SH1>
                        </XStack>
                      )}
                      <EligibleCardComponent value={value} />
                    </React.Fragment>
                  ))}
                </Stack>
              }
            />
          )}
          <Stack className="bg-white px-3 py-2">
            <PressableWithIntent
              name="AddCustomer"
              className="bg-primary-100 py-4/2 rounded-md"
              opacity
              onPress={handleNavigation}
              eventName="click_action"
              eventPayload={{
                item_name: 'brands_list_add_customer_eligible_tab',
                brand_name: brandName,
                brand_category: brandCategory,
              }}
            >
              <XStack className="items-center justify-center space-x-2">
                <Icon type="AddUserFilled" color={zetColors.neutral.white100} />
                <B2 className="font-[Graphie-Bold] text-white">
                  {i18n.t('shareCardLink.addCustomer')}
                </B2>
              </XStack>
            </PressableWithIntent>
          </Stack>
        </LazyHOC>
      )}
    </>
  )
}

export default SellCard
